import {Atom, ClientAtom, ClientFunctionAtom, isFunctionAtom} from '../expressionAtomModel';
import {AtomDescription, getMethodLabel, getPropertyDescription} from '../expressionLabels';
import {Expression} from '../expressionModel';
import {ExpressionsContext} from '../expressionsContext';
import {ExpressionDependencies} from '../expressionState';
import {PreAtom} from '../iotsCodecs';
import {
  allTypes,
  FunctionStart,
  InferredType,
  Method,
  PropertyGetter,
  ResultType,
  TransformerIdsEnum,
  UnknownMethod,
  unknownType,
} from './internal';

export function buildAtoms(
  preAtoms: ReadonlyArray<PreAtom>,
  rootInfer: InferredType,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
): ReadonlyArray<ClientAtom> {
  const atoms: ClientAtom[] = [];
  let previousInfer = rootInfer;

  preAtoms.forEach((preAtom) => {
    const atom = buildAtom(preAtom, previousInfer, context, dependencies);
    atoms.push(atom);
    previousInfer = atom.inferredType;
  });

  return atoms;
}

export function buildAtom(
  preAtom: PreAtom,
  previousInfer: InferredType,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
): ClientAtom {
  const type = getResultType(previousInfer);
  const transformer = getTransformer(type, preAtom.transform);

  return transformer.infer(preAtom, previousInfer, context, dependencies);
}

export function getAtomTransformer(atom: Atom): Method {
  if (atom.transform === TransformerIdsEnum.FUNCTION_START) {
    return new FunctionStart();
  }

  const type = getResultType(atom.previousInfer);
  return getTransformer(type, atom.transform);
}

function getTransformer(type: ResultType, transform: TransformerIdsEnum) {
  if (transform === TransformerIdsEnum.GET) {
    return new PropertyGetter(type);
  }

  const {methods} = type;
  const method = methods && methods[transform];
  if (!method) {
    return new UnknownMethod();
  }

  return method;
}

export function getResultType(inferredType: InferredType | undefined): ResultType {
  if (!inferredType) {
    return unknownType;
  }

  return allTypes[inferredType.type] || unknownType;
}

export function buildAtomDescription(atom: ClientAtom): AtomDescription {
  const {transform, params} = atom;

  if (transform === TransformerIdsEnum.GET) {
    return getPropertyDescription(String(params.property), atom.previousInfer.type);
  }

  return getMethodLabel(transform);
}

export function getExpressionsMaxIndexByAtom(
  expressions: ReadonlyArray<Expression>,
  atom: ClientAtom | ClientFunctionAtom | undefined,
) {
  const defaultIndex = expressions.length;
  if (atom) {
    const index = expressions.findIndex((expression) => hasAtom(expression.atoms, atom));
    if (index === -1) {
      return defaultIndex;
    }

    return index;
  }

  return defaultIndex;
}

function hasAtom(
  atoms: ReadonlyArray<ClientAtom | ClientFunctionAtom>,
  atom: ClientAtom | ClientFunctionAtom,
): boolean {
  return atoms.some((a) => {
    if (a.clientId === atom.clientId) {
      return true;
    }

    if (isFunctionAtom(a)) {
      return hasAtom(a.functionAtoms, atom);
    }

    return false;
  });
}
